import { FC } from "react"
import { createUseStyles } from "react-jss"
import colors from "../../styles/colors"
import { safelyRenderHtml } from "@/util/safelyRenderHtml"

const useStyles = createUseStyles({
  wrapper: {
    lineHeight: "1.75",
    fontSize: "1.125rem",
    maxWidth: "1024px",
    margin: "auto",
    "& h2": {
      margin: "60px 0 24px",
      fontSize: "1.875rem",
      lineHeight: "2.25rem",
      textAlign: "center",
      fontFamily: "var(--font-merriweather), sans-serif",
    },
    "& h3": {
      margin: "30px 0 20px",
      fontFamily: "var(--font-merriweather), sans-serif",
    },
    "& p": {
      fontFamily: "var(--font-inter), sans-serif",
    },
  },
  heroImage: {
    display: "flex",
    justifyContent: "center",
    margin: "2rem 0",
    "& img": {
      borderRadius: "0.5rem",
      boxShadow:
        "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    },
  },
})

interface HeroImage {
  url: string
  alt: string
  width: number
  height: number
}

interface PetShippingDiscoverProps {
  petShippingData: string
  heroImage?: HeroImage | null
  cityName?: string
  stateName?: string
}

const PetShippingDiscover: FC<PetShippingDiscoverProps> = ({
  petShippingData,
  heroImage,
  cityName,
  stateName,
}) => {
  const classes = useStyles()

  if (!petShippingData) {
    return null
  }

  const insertHeroImageAfterH2 = (html: string): string => {
    if (!heroImage) return html

    const maxWidth = 600
    const scaledWidth = Math.min(heroImage.width, maxWidth)
    const scaledHeight = Math.floor(
      (scaledWidth * heroImage.height) / heroImage.width,
    )

    const heroImageHtml = `
      <div class="${classes.heroImage}">
        <img 
          src="${heroImage.url}" 
          alt="${heroImage.alt}"
          width="${scaledWidth}"
          height="${scaledHeight}"
        />
      </div>
    `

    // Find the first closing h2 tag and insert the hero image after it
    const h2ClosingTagIndex = html.indexOf("</h2>")
    if (h2ClosingTagIndex === -1) {
      // If no h2 tag found, add one at the beginning with city and state name
      const defaultTitle =
        cityName && stateName
          ? `Discover ${cityName}, ${stateName}`
          : "Discover the City"
      return `<h2>${defaultTitle}</h2>${heroImageHtml}${html}`
    }

    return (
      html.slice(0, h2ClosingTagIndex + 5) +
      heroImageHtml +
      html.slice(h2ClosingTagIndex + 5)
    )
  }

  return (
    <div className={classes.wrapper} id="discover-state-city">
      {safelyRenderHtml(insertHeroImageAfterH2(petShippingData))}
    </div>
  )
}

export default PetShippingDiscover
